import { useRef, useMemo, useEffect, useState } from 'react';
import { Button, Select } from 'semantic-ui-react';
import styled from '@emotion/styled';
import Swal from 'sweetalert2';
import { QRCodeCanvas } from 'qrcode.react';
import crecerFormsInstance from 'src/services/api/crecer';
import EventService from 'src/services/api/eventService';
import DateRangePicker from '../../../components/common/DateRangePicker';
import useDateRange from '../../../components/common/useDateRange';
import {
  CrecerReportContainer,
  CrecerFilters,
  reportTypes,
  crecerSteps,
  seminaryEvent,
} from '../crecerShared';
import ViewHistoricalQR, { getSelfAttendanceUrl } from './viewHistoricalQR';
import { searchSelectProps } from 'src/components/helpers';

const crecerStepToEvent = {
  conectar: 'crecerConectar',
  encuentro: 'crecerEncuentro',
  vida: 'crecerVida',
  influencia: 'crecerInfluence',
  bautizo: 'crecerBautizo',
};

const GuestSelfAttendanceContainer = styled(CrecerReportContainer)`
  .qr-section {
    margin-top: 25px;
    padding: 20px;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 15px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    width: 100%;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }

  .qr-canvas-wrapper {
    background: white;
    padding: 20px;
    border-radius: 12px;
    margin: 15px auto;
    display: inline-block;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);

    canvas {
      display: block;
    }
  }

  .qr-url-text {
    font-size: 0.85rem;
    word-break: break-all;
    color: rgba(255, 255, 255, 0.9);
    margin-top: 10px;
  }

  .grid-section {
    width: 100%;
    margin-top: 20px;
    padding: 0 10px 20px;
  }
`;

const GuestSelfAttendance = () => {
  const [inputs, setInputs] = useState({
    type: reportTypes[0].value,
    step: crecerSteps[0].value,
    seminary: seminaryEvent[0].value,
    classId: '',
  });
  const [eventDates, setEventDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [loading, setLoading] = useState(false);
  const [qrUrl, setQrUrl] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const qrRef = useRef(null);

  const dateRangeHook = useDateRange({ useStartOfWeek: true });

  useEffect(() => {
    EventService.getDates()
      .then((response) => {
        if (response?.data?.success && response.data.dates) {
          setEventDates(response.data.dates);
        }
        setLoadingDates(false);
      })
      .catch(() => {
        setLoadingDates(false);
      });
  }, []);

  const currentEvent = useMemo(() => {
    if (inputs.type === 'crecer') return crecerStepToEvent[inputs.step] || null;
    if (inputs.type === 'seminarios') return inputs.seminary || null;
    return null;
  }, [inputs.type, inputs.step, inputs.seminary]);

  const classOptions = useMemo(() => {
    if (!currentEvent) return [];
    const list = eventDates.filter((d) => d.event === currentEvent);
    return list.map((d) => ({
      key: d.id,
      text: d.attendanceName,
      value: String(d.id),
    }));
  }, [eventDates, currentEvent]);

  useEffect(() => {
    const valid = classOptions.some((o) => o.value === inputs.classId);
    if (!valid && classOptions.length > 0) {
      setInputs((prev) => ({ ...prev, classId: classOptions[0].value }));
    }
  }, [classOptions, inputs.classId]);

  const selectHandle = (e, { value, name }) => {
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerateQR = () => {
    if (!dateRangeHook.startDate || !dateRangeHook.endDate) {
      Swal.fire('Selecciona el rango de fechas');
      return;
    }

    setLoading(true);
    setQrUrl(null);

    const body = {
      startDate: dateRangeHook.startDateTimestamp,
      endDate: dateRangeHook.endDateTimestamp,
      type: inputs.type,
    };
    if (inputs.type === 'crecer') body.step = inputs.step;
    if (inputs.type === 'seminarios') body.seminary = inputs.seminary;
    const classId = inputs.classId || classOptions[0]?.value;
    if (classId) {
      const selectedEventDate = eventDates.find((d) => String(d.id) === classId);
      if (selectedEventDate?.attendanceCode) {
        body.attendanceCode = selectedEventDate.attendanceCode;
      }
    }

    crecerFormsInstance
      .getGuestSelfAttendanceQRUrl(body)
      .then((response) => {
        const token = response?.data?.token;
        if (token) {
          setQrUrl(getSelfAttendanceUrl(token));
        } else {
          Swal.fire('El servidor no devolvió una URL para el código QR.');
        }
        setLoading(false);
      })
      .catch((error) => {
        if (error?.response?.data?.message) {
          Swal.fire(error.response.data.message);
        } else {
          Swal.fire('Error al generar el código QR.');
        }
        setLoading(false);
      });
  };

  const handleDownloadQR = () => {
    if (!qrRef.current || !qrUrl) return;
    const canvas = qrRef.current.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'codigo-qr-autoregistro.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const canSubmit =
    dateRangeHook.startDate &&
    dateRangeHook.endDate &&
    (classOptions.length === 0 || inputs.classId || classOptions[0]?.value);

  if (showHistory) {
    return (
      <GuestSelfAttendanceContainer>
        <ViewHistoricalQR embedded onBack={() => setShowHistory(false)} />
      </GuestSelfAttendanceContainer>
    );
  }

  return (
    <GuestSelfAttendanceContainer>
      <div className="report-content">
        <h1>Código QR de autoregistro</h1>
        <p className="small">
          <small>
            Selecciona el rango de fechas y detalles para generar un código QR válido para ese
            período.
          </small>
        </p>

        <div className="filters-section">
          <div className="date-range-section">
            <label htmlFor="qrDate">Rango de fechas</label>
            <p className="small">
              <small>
                Rango de fechas en el que el QR será válido para autoregistro. <br /> En caso de que
                solo sea un dia, pon la misma fecha en inicio y fin.
              </small>
            </p>
            <DateRangePicker
              startDate={dateRangeHook.startDate}
              endDate={dateRangeHook.endDate}
              onStartDateChange={dateRangeHook.handleStartDateChange}
              onEndDateChange={dateRangeHook.handleEndDateChange}
              startDateLabel="Fecha inicio"
              endDateLabel="Fecha fin"
            />
          </div>
          <CrecerFilters inputs={inputs} onSelectChange={selectHandle} />

          {!loadingDates && classOptions.length > 0 && (
            <div className="selectorCheck">
              <Select
                placeholder="Clase / evento"
                {...searchSelectProps({
                  name: 'classId',
                  value: inputs.classId || classOptions[0]?.value,
                  handler: selectHandle,
                  options: classOptions,
                })}
              />
            </div>
          )}

          <Button
            secondary
            disabled={loading || !canSubmit}
            loading={loading}
            onClick={handleGenerateQR}
          >
            {loading ? 'Generando...' : 'Generar código QR'}
          </Button>
          <Button primary onClick={() => setShowHistory(true)}>
            Ver historial de QR
          </Button>

          {qrUrl && (
            <div className="qr-section" ref={qrRef}>
              <div className="qr-canvas-wrapper">
                <QRCodeCanvas value={qrUrl} size={256} level="M" />
              </div>
              <p className="qr-url-text">{qrUrl}</p>
              <Button secondary onClick={handleDownloadQR}>
                Descargar QR
              </Button>
            </div>
          )}
        </div>
      </div>
    </GuestSelfAttendanceContainer>
  );
};

export default GuestSelfAttendance;
